import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const BettingModal = ({ bet, match, onClose, onPlaceBet }) => {
  const [amount, setAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const quickAmounts = [100, 500, 1000, 2000];
  const potentialWin = amount
    ? (amount * parseFloat(bet.odds)).toFixed(2)
    : "0.00";

  const handleBet = async () => {
    if (!amount) return;
    setError("");
    setIsLoading(true);

    try {
      await onPlaceBet(bet.name, amount, potentialWin, bet.odds);
      onClose();
    } catch (err) {
      setError("Failed to place bet. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === "" || (/^\d*\.?\d{0,2}$/.test(value) && value <= 10000)) {
      setAmount(value);
      setError("");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-gray-800 p-6 rounded-xl shadow-2xl w-96 border border-gray-700">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-white">{bet.name}</h2>
            <p className="text-sm text-gray-400">Odds: {bet.odds}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Match Info */}
        <div className="bg-gray-700/50 rounded-lg p-3 mb-4">
          <div className="text-sm text-gray-300">{match.tournament}</div>
          <div className="text-white font-medium">
            {match.team1.name} vs {match.team2.name}
          </div>
        </div>

        {/* Amount Input */}
        <div className="mb-4">
          <label className="block text-gray-400 text-sm font-medium mb-2">
            Bet Amount
          </label>
          <input
            type="number"
            className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            value={amount}
            onChange={handleAmountChange}
            placeholder="Enter amount"
            min="0"
            max="10000"
          />
          <div className="grid grid-cols-4 gap-2 mt-2">
            {quickAmounts.map((quickAmount) => (
              <button
                key={quickAmount}
                onClick={() => setAmount(quickAmount)}
                className="bg-gray-700/50 text-gray-300 py-1 px-2 rounded border border-gray-600 hover:bg-gray-700 text-sm"
              >
                ₹{quickAmount}
              </button>
            ))}
          </div>
        </div>

        {/* Potential Win */}
        <div className="bg-gray-700/30 rounded-lg p-3 mb-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Potential Win:</span>
            <span className="text-green-400 font-bold">₹{potentialWin}</span>
          </div>
        </div>

        {error && <div className="text-red-500 text-sm mb-4">{error}</div>}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            className="flex-1 bg-gray-700 text-white py-2.5 rounded-lg hover:bg-gray-600 transition-colors text-sm"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="flex-1 bg-green-600 text-white py-2.5 rounded-lg hover:bg-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            onClick={handleBet}
            disabled={!amount || isLoading}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Processing...
              </span>
            ) : (
              "Place Bet"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const BettingCategory = ({ title, description, options, onBetClick }) => (
  <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-lg p-3 mb-3">
    <h3 className="text-base font-semibold text-white mb-1">{title}</h3>
    {description && <p className="text-xs text-gray-400 mb-2">{description}</p>}
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {options.map((option, index) => (
        <button
          key={index}
          onClick={() => onBetClick(option)}
          className="bg-gray-700/50 hover:bg-blue-600 border border-gray-600 rounded-lg p-2 transition-all group"
        >
          <div className="text-xs text-gray-300 group-hover:text-white">
            {option.name}
          </div>
          <div className="text-sm font-bold text-white">{option.odds}</div>
        </button>
      ))}
    </div>
  </div>
);

const MatchDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedBet, setSelectedBet] = useState(null);

  console.log("Match ID:", id);

  // You would typically fetch match details using the ID
  // For now using static data
  const match = {
    id: "all-2024-sl-ind",
    tournament: "T20 Asian Legends League",
    team1: {
      name: "Srilankan Lions",
      score: "148/7",
      overs: "20.0",
    },
    team2: {
      name: "Indian Royals",
      score: "Yet to bat",
    },
  };

  const bettingCategories = [
    {
      title: "Winner (Incl. Super Over)",
      description:
        "Bet on the match winner, even if it goes into a Super Over.",
      options: [
        { name: "Srilankan Lions", odds: "1.70" },
        { name: "Indian Royals", odds: "2.10" },
      ],
    },
    {
      title: "Most Sixes",
      description: "Bet on the team hitting the most sixes.",
      options: [
        { name: "Srilankan Lions", odds: "1.65" },
        { name: "Indian Royals", odds: "2.65" },
        { name: "Draw", odds: "7.25" },
      ],
    },
    {
      title: "1st Innings Over 1 - 3rd Delivery",
      description:
        "Betting on total runs scored on the 3rd delivery of the 1st over.",
      options: [
        { name: "Over 0.5", odds: "2.00" },
        { name: "Under 0.5", odds: "1.65" },
        { name: "Over 1.5", odds: "4.50" },
        { name: "Under 1.5", odds: "1.12" },
        { name: "Over 3.5", odds: "4.95" },
        { name: "Under 3.5", odds: "1.10" },
      ],
    },
    {
      title: "1st Innings Over 1 - 4th Delivery",
      description: "Similar to the previous section but for the 4th delivery.",
      options: [
        { name: "Over 0.5", odds: "2.10" },
        { name: "Under 0.5", odds: "1.70" },
        { name: "Over 1.5", odds: "4.60" },
        { name: "Under 1.5", odds: "1.15" },
        { name: "Over 3.5", odds: "5.00" },
        { name: "Under 3.5", odds: "1.12" },
      ],
    },
  ];

  const handleBetClick = (bet) => {
    setSelectedBet(bet);
  };

  const handlePlaceBet = async (selection, amount, potentialWin, odds) => {
    try {
      // Implement actual bet placement logic here
      console.log("Bet placed:", { selection, amount, potentialWin, odds });

      // Show success message
      toast.success("Bet placed successfully!");

      // Redirect to bet history page
      setTimeout(() => {
        navigate("/bet-history");
      }, 2000);
    } catch (error) {
      toast.error("Failed to place bet. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 pb-4">
      <div className="">
        {/* Match Summary Card */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-3 gap-3 items-center">
            <div className="text-center">
              <div className="text-base font-bold text-white">
                {match.team1.name}
              </div>
              <div className="text-xl font-bold text-white">
                {match.team1.score}
              </div>
              <div className="text-xs text-gray-400">
                {match.team1.overs} overs
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-400 mb-1">
                {match.tournament}
              </div>
              <div className="text-[10px] px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full inline-block">
                Live
              </div>
            </div>
            <div className="text-center">
              <div className="text-base font-bold text-white">
                {match.team2.name}
              </div>
              <div className="text-xl font-bold text-white">
                {match.team2.score}
              </div>
              <div className="text-xs text-gray-400">&nbsp;</div>
            </div>
          </div>
        </div>

        {/* Betting Categories */}
        <div className="space-y-3">
          {bettingCategories.map((category, index) => (
            <BettingCategory
              key={index}
              {...category}
              onBetClick={handleBetClick}
            />
          ))}
        </div>

        {selectedBet && (
          <BettingModal
            bet={selectedBet}
            match={match}
            onClose={() => setSelectedBet(null)}
            onPlaceBet={handlePlaceBet}
          />
        )}
      </div>
    </div>
  );
};

export default MatchDetailsPage;
